import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bookmark } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import type { GameRow, Sport, MarketType, DateFilter, StatusFilter, MarketSide, PriceMode, TeamSide, MultiOutcomeEvent, SeriesOutcome } from './types';
import { CFB_BUCKET_LABEL, MULTI_OUTCOME_SPORTS, SPORT_LABEL, SPORT_TABS } from './types';

function todayMdy(): string {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

function mdyToIso(mdy: string): string {
  const [mm, dd, yyyy] = mdy.split('/');
  return `${yyyy}-${mm}-${dd}`;
}

function isoToMdy(iso: string): string {
  const [yyyy, mm, dd] = iso.split('-');
  return yyyy && mm && dd ? `${mm}/${dd}/${yyyy}` : iso;
}

function startOfIsoDayMs(iso: string): number {
  const [yyyy, mm, dd] = iso.split('-').map(Number);
  if (!yyyy || !mm || !dd) return Number.NaN;
  return new Date(yyyy, mm - 1, dd).getTime();
}

function gameSortMs(game: GameRow): number {
  if (game.startTimeMs != null && Number.isFinite(game.startTimeMs)) {
    return game.startTimeMs;
  }
  const dayMs = startOfIsoDayMs(game.date);
  return Number.isFinite(dayMs) ? dayMs : Number.POSITIVE_INFINITY;
}

const SPORT_ORDER = new Map<Sport, number>(SPORT_TABS.map((tab, index) => [tab.id, index]));

function sortSports(sports: Iterable<Sport>): Sport[] {
  return Array.from(sports).sort((a, b) => (SPORT_ORDER.get(a) ?? 999) - (SPORT_ORDER.get(b) ?? 999));
}

function sportSelectionLabel(sports: Sport[], allSports: boolean): string {
  if (allSports) return 'All Sports';
  if (sports.length === 1) return SPORT_LABEL[sports[0]] ?? sports[0].toUpperCase();
  if (sports.length === 2) return sports.map((sport) => SPORT_LABEL[sport] ?? sport.toUpperCase()).join(' + ');
  return `${sports.length} Sports`;
}

/** The #248 focus contract's own query keys, in a fixed order.
 *
 *  The dashboard URL carries other things (today just `sport`, tomorrow
 *  whatever a future feature adds), and the parsed focus `identity` is the
 *  one-shot key for hydration — so an unrelated param must never be able to
 *  reset it. Projecting the query through this list, in THIS order, makes the
 *  identity depend on the focus contract alone and on nothing else, whatever
 *  order the keys arrived in. Repeats are preserved deliberately: a repeated
 *  focus key is a contradiction the parser has to see and reject. */
const FOCUS_QUERY_KEYS = ['focus', 'sport', 'date', 'event', 'ticker', 'venue', 'side', 'reason'] as const;

function focusedQueryString(params: URLSearchParams): string {
  const focused = new URLSearchParams();
  for (const key of FOCUS_QUERY_KEYS) {
    for (const value of params.getAll(key)) focused.append(key, value);
  }
  return focused.toString();
}

/** Handed to the hydration hook once the trader has deliberately taken the
 *  board somewhere else — see `focusTakenOver`. */
const NO_FOCUS: ParsedDashboardFocus = { kind: 'none' };

/** Which board a focused link's target lives on (#539).
 *
 *  Derived from the TICKER rather than read from a query key, deliberately. The
 *  ticker already determines the market's family, so a `kind=` parameter would
 *  be a second spelling of the same fact and the two could disagree — a link
 *  claiming `kind=total` for a moneyline ticker would land on a board that can
 *  never contain it. This mirrors Game Detail, whose `?ticker=&side=` deep link
 *  likewise names no kind and lets each section recognize its own markets. */
function focusedBoardFor(ticker: string): MarketType {
  return marketKindFromTicker(ticker) === 'moneyline' ? 'moneyline' : 'spread-total';
}

function shiftMdy(mdy: string, days: number): string {
  const [mm, dd, yyyy] = mdy.split('/').map(Number);
  const d = new Date(yyyy, mm - 1, dd);
  d.setDate(d.getDate() + days);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}
import { DashboardToolbar } from './DashboardToolbar';
import { GameTable, NO_HIDDEN_EMPTY_SPORTS } from './GameTable';
import { LT2_DASH_ML_CARD_SCOPE } from './lt2Moneyline';
import { SeriesTable } from './SeriesTable';
import { MultiOutcomeTable } from './MultiOutcomeTable';
import { SpreadsTotals } from '../SpreadsTotals';
import { GameDetailPanel, type TradeIntent, type BetLineContext } from './GameDetailPanel';
import { buildMultiOutcomeBetContext, synthesizeFromMultiOutcome } from './multiOutcome';
import { bestPriceCentsForOrderSide } from './tradeModalBook';
import { PositionSidebar, PositionsWidget } from './PositionSidebar';
import { useMarketStore, useConsensusFor, useConsensusTrendFor, useOpOddsFor, useOpTrendFor, useOpLastAnchorFairFor } from '../../stores/marketStore';
import { buildOpFairView } from '../../lib/opOdds';
import { sportIsServable } from '../../clients/feedConfig';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useAccountStore } from '../../stores/accountStore';
import { usePolyAccountStore } from '../../stores/polyAccountStore';
import { usePolymarketRelayOrders } from '@/hooks/usePolymarketRelayOrders';
import { usePolymarketRelayPositions } from '@/hooks/usePolymarketRelayPositions';
import {
  activityEvidenceIsAuthoritative,
  polyActivityPollingEnabled,
} from './emptyTennisEvidence';
import { useAuthStore } from '@/stores/authStore';
import {
  useSavedGames,
  gameMarkId,
  collapsedGameKey,
  isRowSaved,
  selectOwnedGames,
  type SavedGame,
} from '@/stores/savedGames';
import { useMinimizedGames, isRowMinimized } from '@/stores/minimizedGames';
import { useUserKalshiPOS } from '@/hooks/useUserKalshiPOS';
import { QueueVintageBadge } from '@/components/atoms/QueueVintageBadge';
import { useEdgeBotsByTicker } from '@/hooks/useEdgeBotsByTicker';
import { useArchivedGames, type ArchivedGame } from './useArchivedGames';
import { parseTicker } from '@/lib/kalshiTicker/parse';
import {
  baseGameKey,
  composeHydraGameKey,
  toHydraGameKey,
  toHydraGameKeyLive,
} from '@/services/analyticsGameKey';
import { useGameStatesPoll } from '@/hooks/useGameStatesPoll';
import { useGameStateFor } from '@/stores/gameStatesStore';
import { useFeedFreshness } from '@/hooks/useFeedFreshness';
import {
  parseFocusedDashboardQuery,
  type ParsedDashboardFocus,
} from '@/lib/routing/focusedDashboard';
import {
  ATP_CHALLENGER_DASHBOARD_SPORT,
  cfbBucketOf,
  dashboardSportFromParam,
  dashboardSportMatches,
  isAtpChallengerRow,
  underlyingSport,
  type DashboardSport,
} from '@/lib/routing/dashboardSport';
import {
  useFocusedDashboardHydration,
  type UseFocusedDashboardHydrationInput,
  type ExternalFocusResolution,
} from './useFocusedDashboardHydration';
import { marketKindFromTicker } from '../PositionTracker/marketKind';
import { useOneClickStore } from '@/stores/oneClickStore';
import {
  tryOneClickFromMoneylineCtx,
  tryOneClickFromMultiOutcomePrice,
  tryOneClickFromTicketCtx,
} from './oneClickIntercept';
import { OneClickArmControl } from './OneClickArmControl';
import { Lt2HotkeyListener } from '@/components/Lt2HotkeyListener';
import { Lt2TargetReadout } from '@/components/Lt2TargetReadout';
import { useLt2Access } from '@/hooks/useLt2Access';
import { useOneClickAccess } from '@/hooks/useOneClickAccess';

interface ValueDashboardProps {
  /** Lets the floating positions popup open the full Position Tracker tab. */
  onOpenPositionsPage?: () => void;
}

// Activity-window for the chip-change resnap heal. Passed to
// requestResnapshotIfStale, which fires a snapshot only when the share
// of tickers with any frame in the last CHIP_RESNAP_STALE_MS falls below
// ~10% — i.e. the feed is mostly-dead, not just one chatty ticker
// keeping the global timestamp warm. 60 s window so quiet-but-healthy
// markets still register as active.
const CHIP_RESNAP_STALE_MS = 60_000;

export function ValueDashboard({ onOpenPositionsPage }: ValueDashboardProps = {}) {
  // ── Hydra data (connection managed at App level) ───────────────────
  // Feed freshness still drives the empty-state "Connecting to Hydra…" copy;
  // the stale/seconds readout moved to the global StatusBar.
  const { connected } = useFeedFreshness();
  const getGames = useMarketStore((s) => s.getGames);
  const getMultiOutcomeEvents = useMarketStore((s) => s.getMultiOutcomeEvents);
  const getAdvanceEvents = useMarketStore((s) => s.getAdvanceEvents);
  const getSeriesEvents = useMarketStore((s) => s.getSeriesEvents);
  const getActiveSports = useMarketStore((s) => s.getActiveSports);
  const requestResnapshotIfStale = useMarketStore((s) => s.requestResnapshotIfStale);
  const version = useMarketStore((s) => s.version);

  // ── Saved games (#120) ─────────────────────────────────────────────
  // Browser-local, per-user. The list can't be read at module load because
  // we may not know who is signed in yet, so point the store at the current
  // user here and re-point it whenever that changes (login, logout, or a
  // cross-tab logout adopted by authStore's storage listener). Passing null
  // clears the in-memory list without erasing what's persisted, so signing
  // back in restores it.
  const authUserId = useAuthStore((s) => s.user?.id ?? null);
  const authOrgId = useAuthStore((s) => s.user?.orgId ?? null);
  const hydrateSavedGames = useSavedGames((s) => s.hydrateFor);
  useEffect(() => {
    hydrateSavedGames(authUserId);
  }, [authUserId, hydrateSavedGames]);
  const savedIds = useSavedGames((s) => s.ids);
  const savedCollapsedIds = useSavedGames((s) => s.collapsedIds);
  const savedOwnerId = useSavedGames((s) => s.userId);
  const savedIndex = useMemo(
    () => ({ userId: savedOwnerId, ids: savedIds, collapsedIds: savedCollapsedIds }),
    [savedOwnerId, savedIds, savedCollapsedIds],
  );
  // Ownership-gated: between a logout and the next hydration the loaded list
  // belongs to nobody, and this yields empty rather than the last user's games.
  const savedEntries = useSavedGames((s) => selectOwnedGames(s, authUserId));
  const removeSavedGame = useSavedGames((s) => s.remove);
  // Total saved across all sports/dates — distinguishes "you haven't saved
  // anything" from "your saved games just aren't on this tab", which the
  // filter-respecting behavior above makes a real question for the user.
  const savedCount = savedEntries.length;

  // ── Minimized games (#654) ─────────────────────────────────────────
  // Same per-user lifecycle as the saved list above, and for the same reason:
  // the list can't be read at module load, and a list belonging to the
  // previous account must never reach the screen.
  const hydrateMinimizedGames = useMinimizedGames((s) => s.hydrateFor);
  useEffect(() => {
    hydrateMinimizedGames(authUserId);
  }, [authUserId, hydrateMinimizedGames]);
  const minimizedIds = useMinimizedGames((s) => s.ids);
  const minimizedCollapsedIds = useMinimizedGames((s) => s.collapsedIds);
  const minimizedOwnerId = useMinimizedGames((s) => s.userId);
  const minimizedIndex = useMemo(
    () => ({
      userId: minimizedOwnerId,
      ids: minimizedIds,
      collapsedIds: minimizedCollapsedIds,
    }),
    [minimizedOwnerId, minimizedIds, minimizedCollapsedIds],
  );
  const restoreAllMinimized = useMinimizedGames((s) => s.restoreAll);
  const toggleMinimizedGame = useMinimizedGames((s) => s.toggle);

  // Landing-page defaults — read once at mount time so changing the pref in
  // Settings doesn't yank the active view out from under the user.
  const initialMarketType = useDisplaySettings.getState().defaultMarketType;
  const initialPriceMode = useDisplaySettings.getState().defaultPriceMode;

  // ── Toolbar state ──────────────────────────────────────────────────
  // The sport filter lives in the URL (?sport=nba | ?sport=all; absent →
  // the user's saved default). Reload keeps you where you were, links are
  // shareable, and Back from GameDetail restores the sport natively through
  // history — this replaced the sessionStorage returnSport stash (TTL +
  // StrictMode peek/clear dance, deleted). Chip clicks write with
  // replace:true so they don't pile up history entries.
  const [searchParams, setSearchParams] = useSearchParams();
  const [defaultSport] = useState(() => useDisplaySettings.getState().defaultSport);
  const sportParam = searchParams.get('sport');
  const allSports = sportParam === 'all';
  // #604/#653 — the URL's sport key may name a dashboard-only board: the ATP
  // Challenger circuit or a CFB subdivision bucket. Everything authority-shaped
  // (getGames, display settings, shared sport APIs) runs on the underlying
  // Sport; only tabs, row filtering, and focused URLs see the DashboardSport.
  const selectedDashboardSport: DashboardSport =
    dashboardSportFromParam(sportParam) ?? defaultSport;
  const selectedSport: Sport = underlyingSport(selectedDashboardSport);
  // Moneyline sport filter. Empty set means "All sports" (?sport=all);
  // non-empty means the single active moneyline sport. Futures and
  // spread/total are single-sport and read selectedSport — which falls back
  // to the saved default while 'all' is active.
  const selectedSports = useMemo<ReadonlySet<Sport>>(
    () => (allSports ? new Set<Sport>() : new Set([selectedSport])),
    [allSports, selectedSport],
  );
  // ── Focused deep link (#248, extended to CFB by #539) ──────────────
  // A Position Tracker graph chip encodes the exact market it already knows
  // into this URL; the board re-derives that market from live rows and opens
  // the EXISTING panel through it. Parsing is pure and lives in
  // lib/routing/focusedDashboard; the one-shot state machine lives in
  // useFocusedDashboardHydration. Everything here is wiring.
  //
  // Note the sport filter needs no focus-specific handling: the contract's
  // `sport` key IS the dashboard's own `?sport=` param, so `selectedSport`
  // above already lands on the routed sport for a focused URL.
  //
  // The BOARD does need handling (#539): a spread or total target lives on the
  // Spread/Total tab, and which tab that is comes from `focusedBoardFor` — the
  // ticker — because the query deliberately carries no market-kind key.
  const focusQueryKey = focusedQueryString(searchParams);
  const parsedFocus = useMemo(
    () => parseFocusedDashboardQuery(new URLSearchParams(focusQueryKey)),
    [focusQueryKey],
  );
  const focusTarget = parsedFocus.kind === 'market' ? parsedFocus.target : null;
  // The trader has deliberately moved the board (sport, date, market type).
  // From then on the focus is inert for the rest of this mount, INCLUDING
  // across the identity change a sport toggle causes — `sport` is the one
  // focus-contract key the toolbar itself rewrites, so without this latch the
  // hook would see a "new" focus URL and try to hydrate the corrupted one.
  // The query is left untouched either way, so reload still reproduces the
  // original link.
  const [focusTakenOver, setFocusTakenOver] = useState(false);

  const [selectedMarketType, setSelectedMarketType] = useState<MarketType>(() =>
    // Explicit, not "the saved default is probably moneyline already".
    focusTarget ? focusedBoardFor(focusTarget.ticker) : initialMarketType,
  );
  const [selectedDate, setSelectedDate] = useState(() =>
    focusTarget ? isoToMdy(focusTarget.date) : todayMdy(),
  );
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');
  // Default 'all' so FINAL rows show in the natural sort during their
  // ~5-min hydra cache lifetime. The 'active' / 'settled' refinements stay
  // available on the toolbar for users who want a filtered view.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [marketSide, setMarketSide] = useState<MarketSide>(() => focusTarget?.side ?? 'no');
  const [priceMode, setPriceMode] = useState<PriceMode>(initialPriceMode);
  // #212 — the #192 session-local hide toggle became the persisted per-sport
  // suppression list. Read reactively (unlike the mount-time landing prefs)
  // because the toolbar chip must flip the board live; default [] preserves
  // the GBuff show-everything experience until the user opts in.
  const hiddenEmptySports = useDisplaySettings((s) => s.hiddenEmptySports);
  const setHiddenEmptySports = useDisplaySettings((s) => s.setHiddenEmptySports);
  // #509 — same reactive-read reasoning as hiddenEmptySports above: the toolbar
  // chip must flip the board live, and the default `false` preserves today's
  // production experience until the user opts in.
  const kalshiMarketsOnly = useDisplaySettings((s) => s.kalshiMarketsOnly);
  const setKalshiMarketsOnly = useDisplaySettings((s) => s.setKalshiMarketsOnly);
  // #654 — same reactive read, same reason.
  const cfbSavedOnly = useDisplaySettings((s) => s.cfbSavedOnly);
  const setCfbSavedOnly = useDisplaySettings((s) => s.setCfbSavedOnly);
  const selectedDateIso = useMemo(() => mdyToIso(selectedDate), [selectedDate]);
  const selectedSportsKey = useMemo(
    () => sortSports(selectedSports).join('|'),
    [selectedSports],
  );
  const showAllSportsChip = selectedMarketType === 'moneyline';
  const selectedMoneylineSports = useMemo<Sport[]>(() => {
    const sports =
      selectedSports.size === 0
        ? SPORT_TABS.map((tab) => tab.id)
        : sortSports(selectedSports);
    return sports.filter((sport) => !MULTI_OUTCOME_SPORTS.has(sport));
  }, [selectedSports, selectedSportsKey]);
  const singleSelectedMultiOutcomeSport = useMemo<Sport | null>(() => {
    if (selectedMarketType !== 'moneyline' || selectedSports.size !== 1) return null;
    const [sport] = Array.from(selectedSports);
    return MULTI_OUTCOME_SPORTS.has(sport) ? sport : null;
  }, [selectedMarketType, selectedSports, selectedSportsKey]);

  // Re-apply a focused link's board state whenever the focus IDENTITY changes
  // while this component stays mounted (Back/Forward between two focused
  // links, or a second chip click). Mount is already covered by the lazy
  // initializers above, so this is a no-op on first render — React bails out
  // of a state update that changes nothing. Deliberately silent once the
  // trader has taken the board over.
  useEffect(() => {
    if (focusTakenOver || parsedFocus.kind !== 'market') return;
    setSelectedMarketType(focusedBoardFor(parsedFocus.target.ticker));
    setSelectedDate(isoToMdy(parsedFocus.target.date));
    setDateFilter('today');
    setMarketSide(parsedFocus.target.side);
  }, [parsedFocus, focusTakenOver]);

  // Heal stale state on any toolbar interaction. Skips on first render
  // (the connect-time snapshot covers initial load) and is a no-op
  // unless ~90% of tickers have gone silent in the last
  // CHIP_RESNAP_STALE_MS — guards against the failure mode where one
  // chatty ticker keeps the connection looking alive while everything
  // else is throttled or upstream-dead. Pure-display toggles like
  // marketSide / priceMode don't need new data, but firing the check on
  // them is essentially free and catches "I sat idle, came back, clicked
  // anything" stale paths.
  const firstToolbarRender = useRef(true);
  useEffect(() => {
    if (firstToolbarRender.current) {
      firstToolbarRender.current = false;
      return;
    }
    requestResnapshotIfStale(CHIP_RESNAP_STALE_MS);
  }, [
    selectedSport,
    selectedDashboardSport,
    selectedSportsKey,
    selectedMarketType,
    selectedDate,
    dateFilter,
    statusFilter,
    marketSide,
    priceMode,
    requestResnapshotIfStale,
  ]);

  // ── Position sidebar state ─────────────────────────────────────────
  const [showPositionSidebar, setShowPositionSidebar] = useState(false);

  const selectedMoneylineSportSet = useMemo(
    () => new Set(selectedMoneylineSports),
    [selectedMoneylineSports],
  );
  const selectedMoneylineLabelSports = useMemo(
    () => (selectedSports.size === 0 ? selectedMoneylineSports : sortSports(selectedSports)),
    [selectedMoneylineSports, selectedSports, selectedSportsKey],
  );
  // A selected CFB bucket qualifies the board's own name ("CFB · FBS"), so the
  // table header states which slice is on screen on every board, not just
  // moneyline.
  // Null on Futures for the same reason the chips hide there (see the toolbar):
  // a bucket has no meaning on a championship market, so the board must not
  // name one in its title or empty state either.
  const selectedCfbBucket =
    allSports || selectedMarketType === 'series' ? null : cfbBucketOf(selectedDashboardSport);
  // #654 — a CFB MATCHUP board is on screen, as the whole slate or as one
  // bucket. The single owner of that question: it gates the toolbar's CFB
  // controls (both #653's buckets and #654's Saved-only chip) AND the lens
  // filters below, and those must never disagree. Same two exclusions the
  // bucket carries — "All sports" has no sub-board, and a championship future
  // is one market listing many teams rather than a matchup.
  const cfbBoardSelected =
    !allSports &&
    underlyingSport(selectedDashboardSport) === 'cfb' &&
    selectedMarketType !== 'series';
  const selectedCfbBucketLabel = selectedCfbBucket ? CFB_BUCKET_LABEL[selectedCfbBucket] : null;
  const baseSportFilterLabel =
    selectedMarketType === 'moneyline'
      ? selectedDashboardSport === ATP_CHALLENGER_DASHBOARD_SPORT && !allSports
        ? 'ATP Challenger'
        : sportSelectionLabel(selectedMoneylineLabelSports, selectedSports.size === 0)
      : SPORT_LABEL[selectedSport] ?? selectedSport.toUpperCase();
  const sportFilterLabel = selectedCfbBucketLabel
    ? `${baseSportFilterLabel} · ${selectedCfbBucketLabel}`
    : baseSportFilterLabel;
  const tableTitleLabel =
    selectedMarketType === 'moneyline'
      ? `${sportFilterLabel} Moneylines`
      : undefined;

  // ── Data from Hydra via Zustand ─────────────────────────────────────
  // version changes on every RAF tick → forces fresh derivation from mutable maps.
  // Series rows merge cross-venue via canonicalSeriesKey inside marketStore;
  // no client-side merge needed.
  const liveGamesForFilter = useMemo(() => {
    void version; // explicit dependency
    if (selectedMarketType === 'series') return [];
    const sports =
      selectedMarketType === 'moneyline' ? selectedMoneylineSports : [selectedSport];
    const rows = sports.flatMap((sport) => getGames(sport, selectedMarketType, {
      statusFilter,
      dateFilter,
      selectedDateIso,
    }));
    // #604 — split the ATP circuits on a single-sport selection. All-sports
    // deliberately stays unfiltered: the aggregate board keeps both circuits.
    return allSports
      ? rows
      : rows.filter((row) => dashboardSportMatches(row, selectedDashboardSport));
  }, [
    getGames,
    selectedSport,
    selectedDashboardSport,
    allSports,
    selectedMoneylineSports,
    selectedMarketType,
    version,
    statusFilter,
    dateFilter,
    selectedDateIso,
  ]);

  const upcomingFallbackGames = useMemo(() => {
    void version;
    // #248 — a focus query means exact-date mode, always. A stale link must
    // not appear to land on a nearby event, so the fallback is off for every
    // focus kind, valid or not.
    if (parsedFocus.kind !== 'none') return [];
    if (selectedMarketType === 'series' || dateFilter !== 'today' || liveGamesForFilter.length > 0) {
      return [];
    }
    const dayStartMs = startOfIsoDayMs(selectedDateIso);
    if (!Number.isFinite(dayStartMs)) return [];
    const sports =
      selectedMarketType === 'moneyline' ? selectedMoneylineSports : [selectedSport];
    // All upcoming games for the selected sport(s), excluding final/series/archived.
    const upcoming = sports.flatMap((sport) => getGames(sport, selectedMarketType, {
      statusFilter,
      dateFilter: 'all',
      selectedDateIso,
    })).filter((game) => {
      if (game.series || game.archived || game.gameState === 'final') return false;
      // #604 — the upcoming fallback obeys the same circuit split as the
      // live rows; all-sports keeps both circuits.
      if (!allSports && !dashboardSportMatches(game, selectedDashboardSport)) return false;
      return gameSortMs(game) >= dayStartMs;
    });
    if (upcoming.length === 0) return [];
    // Show ONLY the next calendar day that has games — whether that's tomorrow
    // or two months out (off-season / sparse sport). Stop on the first day with
    // games: a dense daily sport (baseball) then shows one day's slate instead
    // of a 7-day pile, and a no-games-today "Today" still isn't a dead end.
    const nextDate = upcoming.reduce(
      (soonest, game) => (game.date < soonest ? game.date : soonest),
      upcoming[0].date,
    );
    return upcoming
      .filter((game) => game.date === nextDate)
      .sort((a, b) => {
        const startCmp = gameSortMs(a) - gameSortMs(b);
        if (startCmp !== 0) return startCmp;
        return a.awayCode.localeCompare(b.awayCode);
      });
  }, [
    getGames,
    selectedSport,
    selectedDashboardSport,
    allSports,
    selectedMoneylineSports,
    selectedMarketType,
    version,
    statusFilter,
    dateFilter,
    selectedDateIso,
    liveGamesForFilter.length,
    parsedFocus.kind,
  ]);

  const usingUpcomingFallback = dateFilter === 'today'
    && liveGamesForFilter.length === 0
    && upcomingFallbackGames.length > 0;
  const liveGames = usingUpcomingFallback ? upcomingFallbackGames : liveGamesForFilter;
  // The single date the fallback landed on (all fallback rows share it) — drives
  // the empty-state copy. null when not in fallback.
  const upcomingFallbackDate = usingUpcomingFallback
    ? upcomingFallbackGames[0]?.date ?? null
    : null;

  // Closed games sourced from the analytics endpoint (Postgres-backed,
  // 30-day lookback). Skipped on the series tab and on non-moneyline
  // market types — analytics endpoint returns moneyline-shaped summaries
  // per game; spread/total surfaces aren't a fit for the skinny-row
  // projection. `statusFilter='active'` also short-circuits since
  // archived rows are by definition non-active.
  const { archived: archivedRaw, loading: archivedLoading } = useArchivedGames(
    selectedMarketType === 'moneyline' && statusFilter !== 'active'
      ? selectedDateIso
      : null,
  );

  const games = useMemo<GameRow[]>(() => {
    // Any FINAL row from the live store gets collapsed into the skinny
    // archived shape so finished games render the same one-liner
    // regardless of whether they came from `_markets` (still in
    // session-cached hydra state) or the analytics fetch. WNBA was the
    // visible offender — its games sit in `_markets` longer than other
    // sports' do post-close, so they kept rendering as full live rows.
    const liveTransformed = liveGames.map((g) =>
      g.gameState === 'final' && !g.archived
        ? {
            ...g,
            archived: {
              kalshiVolume: g.kalshi?.away.volume ?? 0,
              polyVolume: g.polymarket?.away.volume ?? 0,
            },
          }
        : g,
    );
    if (archivedRaw.length === 0) return liveTransformed;
    // Dedupe: drop any analytics row whose canonical gameKey already
    // appears in the live set (live data wins). Live rows use the
    // marketStore's own key shape; normalize through toHydraGameKey so
    // the comparison survives the sport-prefix vs no-prefix variants.
    const liveCanonicalKeys = new Set<string>();
    for (const g of liveTransformed) {
      const k = toHydraGameKey(g.key);
      if (k) liveCanonicalKeys.add(k);
    }
    const archivedRows: GameRow[] = [];
    for (const a of archivedRaw) {
      const sport = a.sport as Sport;
      if (selectedMarketType === 'moneyline') {
        if (!selectedMoneylineSportSet.has(sport)) continue;
      } else if (sport !== selectedSport) continue;
      if (liveCanonicalKeys.has(a.gameKey)) continue;
      archivedRows.push(archivedToGameRow(a));
    }
    if (archivedRows.length === 0) return liveTransformed;
    // Sort archived rows by date desc so newest closed games sit
    // immediately under the live block.
    archivedRows.sort((x, y) => (x.date < y.date ? 1 : x.date > y.date ? -1 : 0));
    return [...liveTransformed, ...archivedRows];
  }, [
    liveGames,
    archivedRaw,
    selectedSport,
    selectedMarketType,
    selectedMoneylineSportSet,
  ]);

  const savedRows = useMemo(
    () => games.filter((g) => isRowSaved(g.key, savedIndex, authUserId)),
    [games, savedIndex, authUserId],
  );

  // Saved games with no row on the board at all — hydra has pruned them and
  // the analytics window (~7 days) no longer covers them, or they're simply
  // on another date. Rendered from the persisted metadata alone so they stay
  // identifiable and removable; deliberately NOT date-filtered, because a
  // saved game the user can only reach by guessing the right date is not
  // removable in any practical sense. Sport filter still applies, so this
  // matches the listed rows' behavior.
  const savedUnlisted = useMemo(() => {
    // Until the feed is up, `games` is empty for reasons that have nothing to
    // do with the saved games — declaring them all "not currently listed" on
    // every page load would be a lie that flickers. Absence is only evidence
    // once there's a connection to be absent from.
    if (!connected) return [];
    const onBoard = new Set<string>();
    for (const g of games) {
      const id = gameMarkId(g.key);
      if (id) onBoard.add(id);
      const collapsed = collapsedGameKey(g.key);
      if (collapsed) onBoard.add(collapsed);
    }
    return savedEntries.filter((e) => {
      if (onBoard.has(e.id)) return false;
      const collapsed = collapsedGameKey(e.id);
      if (collapsed && onBoard.has(collapsed)) return false;
      const sport = e.sport as Sport;
      return selectedMarketType === 'moneyline'
        ? selectedMoneylineSportSet.has(sport)
        : sport === selectedSport;
    });
  }, [
    connected,
    games,
    savedEntries,
    selectedMarketType,
    selectedMoneylineSportSet,
    selectedSport,
  ]);

  const seriesEvents = useMemo(() => {
    void version;
    if (selectedMarketType !== 'series') return [];
    return getSeriesEvents(selectedSport);
  }, [getSeriesEvents, selectedSport, selectedMarketType, version]);

  // Multi-outcome (3-way) sports — soccer Home/Away/Draw. The toolbar's
  // date/status filters apply exactly like the binary table's, including
  // the 7-day upcoming fallback when the selected day has no games.
  const multiOutcomeSport = singleSelectedMultiOutcomeSport;
  const isMultiOutcomeSport = multiOutcomeSport != null;
  const multiEventsForFilter = useMemo(() => {
    void version;
    if (!multiOutcomeSport) return [];
    return getMultiOutcomeEvents(multiOutcomeSport, { statusFilter, dateFilter, selectedDateIso });
  }, [getMultiOutcomeEvents, multiOutcomeSport, version, statusFilter, dateFilter, selectedDateIso]);
  const upcomingFallbackMultiEvents = useMemo(() => {
    void version;
    if (!multiOutcomeSport || dateFilter !== 'today' || multiEventsForFilter.length > 0) {
      return [];
    }
    const dayStartMs = startOfIsoDayMs(selectedDateIso);
    if (!Number.isFinite(dayStartMs)) return [];
    const eventMs = (e: MultiOutcomeEvent) =>
      e.startTimeMs ?? (e.date ? startOfIsoDayMs(e.date) : Number.POSITIVE_INFINITY);
    const upcoming = getMultiOutcomeEvents(multiOutcomeSport, { statusFilter, dateFilter: 'all' })
      .filter((e) => e.gameState !== 'final' && eventMs(e) >= dayStartMs);
    if (upcoming.length === 0) return [];
    // Same rule as the binary fallback: only the next calendar day that has
    // events. Events without a date can't be placed on a day, so they're
    // excluded from the single-day preview.
    let nextDate: string | null = null;
    for (const e of upcoming) {
      if (e.date && (nextDate === null || e.date < nextDate)) nextDate = e.date;
    }
    if (nextDate === null) return [];
    return upcoming.filter((e) => e.date === nextDate);
  }, [
    getMultiOutcomeEvents,
    multiOutcomeSport,
    version,
    statusFilter,
    dateFilter,
    selectedDateIso,
    multiEventsForFilter.length,
  ]);
  const usingMultiUpcomingFallback =
    dateFilter === 'today' &&
    multiEventsForFilter.length === 0 &&
    upcomingFallbackMultiEvents.length > 0;

  // Knockout "To Advance" swap: a knockout game's headline moneyline is the
  // 2-way "who advances" market, not the regulation 1X2. getAdvanceEvents keys
  // its events identically to the 1X2, so swap them in place — group-stage games
  // (no advance market) keep their 3-way unchanged. Everything downstream
  // (rendering, trade clicks, orderbook/POS needs, the detail panel) reads
  // `multiEvents`, so the swap covers them all. See HYDRA_API.md "Soccer To Advance".
  const advanceEventsByKey = useMemo(() => {
    void version;
    const m = new Map<string, MultiOutcomeEvent>();
    if (!multiOutcomeSport) return m;
    for (const e of getAdvanceEvents(multiOutcomeSport)) m.set(e.key, e);
    return m;
  }, [getAdvanceEvents, multiOutcomeSport, version]);
  const baseMultiEvents = usingMultiUpcomingFallback ? upcomingFallbackMultiEvents : multiEventsForFilter;
  const multiEvents = useMemo(
    () =>
      advanceEventsByKey.size === 0
        ? baseMultiEvents
        : baseMultiEvents.map((e) => advanceEventsByKey.get(e.key) ?? e),
    [baseMultiEvents, advanceEventsByKey],
  );
  // The single date the multi fallback landed on — drives the empty-state copy.
  const multiUpcomingFallbackDate = usingMultiUpcomingFallback
    ? upcomingFallbackMultiEvents[0]?.date ?? null
    : null;

  // Sports with ≥1 live market, per hydra's activeSports meta frame.
  // Toolbar uses this to auto-hide chips for sports with no activity today.
  // Always include the currently-selected sport so the active chip doesn't
  // vanish mid-interaction even before the first frame for that sport
  // arrives (and so users can navigate back to it).
  const activeSports = useMemo(() => {
    void version;
    const set = getActiveSports();
    for (const sport of MULTI_OUTCOME_SPORTS) {
      if (getMultiOutcomeEvents(sport).length > 0) set.add(sport);
    }
    set.add(selectedSport);
    for (const sport of selectedSports) set.add(sport);
    // Drop edge-only sports (NFL) that this deploy can't actually serve. Prod
    // hydra's activeSports meta frame advertises NFL, but on a firehose-only
    // lane (no edge configured) no NFL game data arrives — so the chip would be
    // a dead end. Keep the explicitly-selected sport so a deep-link still works.
    for (const sport of [...set]) {
      if (sport !== selectedSport && !sportIsServable(sport)) set.delete(sport);
    }
    return set;
  }, [getActiveSports, getMultiOutcomeEvents, version, selectedSport, selectedSports]);

  // #604 — Challenger chip discovery. Hydra's activeSports meta frame only
  // knows 'atp', so the chip gates on an actual live exact-series row (any
  // date). The currently-selected key stays visible mid-interaction,
  // mirroring `set.add(selectedSport)` above.
  const challengerActive = useMemo(() => {
    void version;
    return getGames('atp', 'moneyline', {
      statusFilter: 'active',
      dateFilter: 'all',
      selectedDateIso,
    }).some(isAtpChallengerRow);
  }, [getGames, version, selectedDateIso]);
  const activeDashboardSports = useMemo<Set<DashboardSport>>(() => {
    const set: Set<DashboardSport> = new Set(activeSports);
    if (challengerActive || selectedDashboardSport === ATP_CHALLENGER_DASHBOARD_SPORT) {
      set.add(ATP_CHALLENGER_DASHBOARD_SPORT);
    }
    return set;
  }, [activeSports, challengerActive, selectedDashboardSport]);
  const selectedDashboardSports = useMemo<ReadonlySet<DashboardSport>>(
    () => (allSports ? new Set<DashboardSport>() : new Set<DashboardSport>([selectedDashboardSport])),
    [allSports, selectedDashboardSport],
  );

  // Sports the "Hide empty" toolbar control offers (#213): the binary moneyline
  // sports actually on the board — selected ∩ active, with soccer already
  // dropped by selectedMoneylineSports. #212 makes only SELECTED sports eligible
  // for suppression; intersecting with activeSports further keeps the menu from
  // advertising sports with no games today, matching the sport chips' own
  // auto-hide. Empty ⇒ the toolbar renders no control. Depends on activeSports,
  // so it lives below that memo. Canonical order inherited from
  // selectedMoneylineSports.
  const eligibleEmptySports = useMemo<Sport[]>(
    () => selectedMoneylineSports.filter((sport) => activeSports.has(sport)),
    [selectedMoneylineSports, activeSports],
  );
  // Toggle one sport's empty-row suppression. The store setter dedupes and
  // re-orders, so an append that duplicates is harmless.
  const toggleHiddenEmptySport = useCallback(
    (sport: Sport) => {
      setHiddenEmptySports(
        hiddenEmptySports.includes(sport)
          ? hiddenEmptySports.filter((s) => s !== sport)
          : [...hiddenEmptySports, sport],
      );
    },
    [hiddenEmptySports, setHiddenEmptySports],
  );
  // "All sports": select or clear every ELIGIBLE sport, deriving the direction
  // from current membership (no persisted all/none flag, per #212). Sports not
  // eligible right now (configured on another board) are preserved either way,
  // so narrowing the filter never silently un-hides them.
  const toggleAllEmptySports = useCallback(() => {
    const allHidden =
      eligibleEmptySports.length > 0 &&
      eligibleEmptySports.every((sport) => hiddenEmptySports.includes(sport));
    if (allHidden) {
      const eligibleSet = new Set(eligibleEmptySports);
      setHiddenEmptySports(hiddenEmptySports.filter((sport) => !eligibleSet.has(sport)));
    } else {
      setHiddenEmptySports([...hiddenEmptySports, ...eligibleEmptySports]);
    }
  }, [eligibleEmptySports, hiddenEmptySports, setHiddenEmptySports]);

  // #509 — the Kalshi-markets-only filter is scoped to the moneyline board, the
  // same fence #212 drew around empty-row suppression. Scoping is made EXPLICIT
  // here rather than left to fall out of `activityReady` (which is only ever
  // true on a moneyline board because the poly reads are gated that way): an
  // implicit coupling would silently widen the filter the day that gate moves.
  const isMoneylineBoard = selectedMarketType === 'moneyline';
  // The effective flag the table filters on — the persisted opt-in AND the
  // board being in scope. Off-board the setting is preserved, never cleared.
  const kalshiMarketsOnlyActive = kalshiMarketsOnly && isMoneylineBoard;
  const toggleKalshiMarketsOnly = useCallback(() => {
    setKalshiMarketsOnly(!kalshiMarketsOnly);
  }, [kalshiMarketsOnly, setKalshiMarketsOnly]);

  // ── The CFB lens (#654) ────────────────────────────────────────────
  // Both halves follow kalshiMarketsOnlyActive's rule: the persisted intent AND
  // the board being in scope. Off-board the settings are preserved, never
  // cleared, so switching sports and back doesn't silently lose them.
  //
  // The minimized filter is board-scoped for a stronger reason than tidiness:
  // the per-row restore control only renders on a lens board, so applying the
  // filter anywhere else would hide a trader's games on a board offering no way
  // to bring them back — exactly what the contract forbids.
  // Narrower than cfbBoardSelected: saved-only is a view over the #120 saved
  // list, which has no control and no strip outside the moneyline board. On
  // Spread/Total it would filter to a set that board cannot populate or clear.
  // (Extending #120's bookmark there is its own change, not this one.)
  const cfbSavedOnlyEligible = cfbBoardSelected && isMoneylineBoard;
  const savedOnlyActive = cfbSavedOnly && cfbSavedOnlyEligible;
  const toggleCfbSavedOnly = useCallback(() => {
    setCfbSavedOnly(!cfbSavedOnly);
  }, [cfbSavedOnly, setCfbSavedOnly]);
  const minimizedLens = useMemo(
    () =>
      cfbBoardSelected
        ? {
            isMinimized: (game: GameRow) =>
              isRowMinimized(game.key, minimizedIndex, authUserId),
            // `toggle` is the restore: the game is minimized (that is why the
            // footer is offering it), so toggling can only clear the mark.
            onRestore: (game: GameRow) => toggleMinimizedGame(game, authUserId),
            onRestoreAll: () => restoreAllMinimized(authUserId),
          }
        : undefined,
    [cfbBoardSelected, minimizedIndex, authUserId, restoreAllMinimized, toggleMinimizedGame],
  );

  // ── Orderbook needs ────────────────────────────────────────────────
  // Declare the set of tickers this view currently renders depth for.
  // The store unions these with other views' needs and hands the union
  // to HydraClient, which narrows the upstream orderbooks subscription.
  // Recompute from `games` (and the series outcomes when that tab's
  // active); `version` already triggers that memo upstream.
  const orderbookTickers = useMemo(() => {
    const set = new Set<string>();
    for (const g of games) {
      if (g.kalshi?.away.marketTicker) set.add(g.kalshi.away.marketTicker);
      if (g.kalshi?.home.marketTicker) set.add(g.kalshi.home.marketTicker);
      if (g.polymarket?.away.marketTicker) set.add(g.polymarket.away.marketTicker);
      if (g.polymarket?.home.marketTicker) set.add(g.polymarket.home.marketTicker);
    }
    for (const event of seriesEvents) {
      for (const outcome of event.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
      }
    }
    for (const event of multiEvents) {
      for (const outcome of event.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
        if (outcome.polymarket?.marketTicker) set.add(outcome.polymarket.marketTicker);
      }
    }
    return Array.from(set);
  }, [games, seriesEvents, multiEvents]);

  // Stable key derived from the ticker set so the effect only re-runs when
  // the actual set changes, not on every RAF tick (orderbookTickers gets a
  // fresh array ref every tick because `games` does).
  const orderbookTickersKey = useMemo(
    () => orderbookTickers.slice().sort().join('|'),
    [orderbookTickers],
  );

  // Kalshi-only ticker subset for the per-user POS hook. We only need
  // queue-position lookups against tickers actually shown on screen — feeding
  // the full set keeps the queue-positions REST call narrow and avoids
  // pulling depth for off-screen orders (e.g. NHL while NBA is selected).
  const kalshiVisibleTickers = useMemo(() => {
    const set = new Set<string>();
    for (const g of games) {
      if (g.kalshi?.away.marketTicker) set.add(g.kalshi.away.marketTicker);
      if (g.kalshi?.home.marketTicker) set.add(g.kalshi.home.marketTicker);
    }
    for (const event of multiEvents) {
      for (const outcome of event.outcomes) {
        if (outcome.kalshi?.marketTicker) set.add(outcome.kalshi.marketTicker);
      }
    }
    return Array.from(set);
  }, [games, multiEvents]);
  const userActivity = useUserKalshiPOS(kalshiVisibleTickers);
  const humanOrdersByTicker =
    userActivity.displayOrdersByTicker ?? userActivity.ordersByTicker;
  // #192/#212 — both poly reads exist ONLY to answer "does the user have
  // exposure on this empty row", so they are gated on the suppression list
  // being ACTIVE for a currently selected moneyline sport, not merely
  // configured. Deselecting or un-hiding must leave no timer behind:
  // revealing rows is the cheap state, and a dashboard parked with no
  // effective suppression should not keep spending the org's relay read
  // budget. Re-activating re-arms both (enabled false → true re-runs each
  // hook's polling effect, which fetches immediately before scheduling the
  // interval).
  const polyActivityEnabled = polyActivityPollingEnabled({
    isMoneylineBoard,
    selectedMoneylineSports,
    hiddenEmptySports,
    kalshiMarketsOnly,
    // #654 — arming the reads is what makes the lens work at all. Without it
    // activityReady never turns true and the filter silently hides nothing,
    // exactly as this module's contract warns. Gated on marks EXISTING, not on
    // the lens merely being available, so a CFB board with an empty mark list
    // spends no read budget.
    cfbMinimizedActive: minimizedLens != null && minimizedIds.size > 0,
  });
  const relayPolyPositions = usePolymarketRelayPositions({
    enabled: polyActivityEnabled,
    includePnl: false,
  });
  const relayPolyOrders = usePolymarketRelayOrders({
    enabled: polyActivityEnabled,
  });
  // Edge bot presence by ticker (row chips). No-op fetch for non-bot users.
  const edgeBots = useEdgeBotsByTicker();

  // Canonical gameKeys the user has any position in — drives the archived-row
  // left-border highlight. Sourced from the FULL per-user position list
  // (accountStore.positions), NOT the ticker-filtered /me/snapshot poller: the
  // highlight must light archived/past-date rows for ANY game the user holds,
  // even ones with no live visible ticker, and the snapshot poller now only
  // returns the visible-ticker subset. Each ticker is parsed via parseTicker
  // (venue-agnostic so PM-* tickers resolve to the same {game, date} shape),
  // and composed into the same alpha-sorted canonical key the analytics
  // endpoint emits. Recomputes only when the position set changes.
  const accountStatus = useAccountStore((s) => s.status);
  const accountPositions = useAccountStore((s) => s.positions);
  const accountOpenOrders = useAccountStore((s) => s.openOrders);
  const personalPolyStatus = usePolyAccountStore((s) => s.status);
  const personalPolyOrders = usePolyAccountStore((s) => s.openOrders);

  const activeKalshiTickers = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const position of accountPositions) {
      if (position.position > 0) set.add(position.ticker);
    }
    for (const order of accountOpenOrders) {
      if (order.remainingCount > 0) set.add(order.ticker);
    }
    return set;
  }, [accountPositions, accountOpenOrders]);

  const activePolyMarketIds = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const position of relayPolyPositions.positions) {
      if (position.netCount > 0) set.add(position.ticker);
    }
    for (const order of relayPolyOrders.orders) {
      if (typeof order.asset_id === 'string' && order.asset_id) set.add(order.asset_id);
      if (typeof order.market === 'string' && order.market) set.add(order.market);
    }
    for (const order of personalPolyOrders) {
      if (order.assetId) set.add(order.assetId);
      if (order.conditionId) set.add(order.conditionId);
    }
    return set;
  }, [relayPolyPositions.positions, relayPolyOrders.orders, personalPolyOrders]);

  // #192 — an EMPTY activity map only justifies hiding a row when every venue
  // read actually answered; see emptyTennisEvidence.ts for the per-source
  // reasoning (notably: `unavailable` is a non-answer, not a "no").
  const activityReady = activityEvidenceIsAuthoritative({
    accountStatus,
    kalshiActivityLoading: userActivity.loading,
    kalshiActivityError: userActivity.error,
    polyPositionsStatus: relayPolyPositions.status,
    polyOrdersStatus: relayPolyOrders.status,
    personalPolyStatus,
  });

  const positionGameKeys = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const p of accountPositions) {
      const parsed = parseTicker(p.ticker);
      if (!parsed.game || !parsed.date) continue;
      const k = composeHydraGameKey({
        date: parsed.date,
        teamA: parsed.game.awayCode,
        teamB: parsed.game.homeCode,
      });
      if (k) set.add(k);
    }
    return set;
  }, [accountPositions]);
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('value-dashboard', orderbookTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('value-dashboard', []);
    };
    // orderbookTickers is intentionally excluded — orderbookTickersKey is the
    // stable identity for the set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderbookTickersKey]);

  // Batched Unabated gameState poll for the visible game list. Drives
  // live scores in the team-name column + (next step) "finished games
  // drop to bottom" via authoritative statusId rather than the existing
  // status/time/price-collapse heuristics. Falls out cleanly when the
  // game list is empty (poller skips fetches with no keys).
  const dashboardGameKeys = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const g of games) {
      // Live poll keys keep the v2 gameNo — an undisambiguated key makes
      // hydra resolve "the live game first", which cross-wires a
      // doubleheader's scores (2026-07-11 MIL@PIT).
      const k = toHydraGameKeyLive(g.key);
      if (k) set.add(k);
    }
    // Multi-outcome (soccer) events too — their pair rows show live scores
    // when Unabated covers the match; misses just render the live dash.
    for (const e of multiEvents) {
      const k = toHydraGameKeyLive(e.key);
      if (k) set.add(k);
    }
    return Array.from(set);
  }, [games, multiEvents]);
  useGameStatesPoll(dashboardGameKeys);

  // ── Detail panel state ──────────────────────────────────────────────
  const [expandedGameKey, setExpandedGameKey] = useState<string | null>(null);
  const [tradeIntent, setTradeIntent] = useState<TradeIntent | null>(null);
  const tradeInteractionSequenceRef = useRef(0);

  // ── Spreads & Totals detail panel state ────────────────────────────
  // When the user expands a spread or total row, SpreadsTotals synthesizes
  // a GameRow for that line and feeds it here; the existing detail panel
  // renders against the synth. `stCloseSignal` bumps when the user closes
  // the panel so SpreadsTotals knows to collapse its inline expansion.
  const [spreadTotalGame, setSpreadTotalGame] = useState<GameRow | null>(null);
  const [spreadTotalBetCtx, setSpreadTotalBetCtx] = useState<BetLineContext | null>(null);
  // Leaving the spread/total tab → drop the synth + chip-strip context.
  // Without this, a totals synth set earlier still drives the side panel
  // on the moneyline tab (expandedGame's null-key path falls back to
  // spreadTotalGame). The panel ends up "stuck" on the prior totals bet
  // until the user explicitly clicks a moneyline pill.
  useEffect(() => {
    if (selectedMarketType !== 'spread-total') {
      setSpreadTotalGame(null);
      setSpreadTotalBetCtx(null);
    }
  }, [selectedMarketType]);
  const [stCloseSignal, setStCloseSignal] = useState(0);

  // #248 — the hydration hook is declared BELOW (it needs handleTradeClick),
  // so the handlers that dismiss a focus reach it through this ref rather
  // than being hoisted past their own dependencies.
  const dismissFocusRef = useRef<() => void>(() => {});
  /** A deliberate board move: dismiss the focus AND stop hydrating for the
   *  rest of this mount. Feed ticks and price-mode toggles must not call it —
   *  they aren't the trader changing context. */
  const takeOverFocus = useCallback(() => {
    dismissFocusRef.current();
    setFocusTakenOver(true);
  }, []);

  const handleToggleExpand = useCallback(
    (key: string, side: TeamSide) => {
      // #248 — the trader is opening a ticket of their own. A focus still
      // waiting on its row must not land on top of it ten seconds later:
      // swapping the panel out from under someone mid-interaction is the
      // surprise the frozen-timeout rule exists to prevent. Dismiss, don't
      // take over — a row click is not a board move, so a genuinely NEW
      // focused link arriving later must still be allowed to hydrate.
      dismissFocusRef.current();
      if (expandedGameKey === key) {
        if (tradeIntent?.side === side) {
          setExpandedGameKey(null);
          setTradeIntent(null);
        } else {
          setTradeIntent({ side, venue: 'kalshi' });
        }
        return;
      }
      setExpandedGameKey(key);
      setTradeIntent({ side, venue: 'kalshi' });
    },
    [expandedGameKey, tradeIntent?.side],
  );

  const handleCloseDetail = useCallback(() => {
    dismissFocusRef.current();
    setExpandedGameKey(null);
    setTradeIntent(null);
    setSpreadTotalGame(null);
    setSpreadTotalBetCtx(null);
    setStCloseSignal((c) => c + 1);
  }, []);

  // Live-trading one-click mode (V1b) — armed state + submit lane consumed by
  // the user click handler below; the toolbar control owns arming/size UI.
  const oneClickArmed = useOneClickStore((s) => s.armed);
  const disarmOneClick = useOneClickStore((s) => s.disarm);
  const oneClickAccess = useOneClickAccess();
  const lt2Access = useLt2Access();
  const lt2HotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);

  // Armed trading never survives an identity or board-context boundary.
  // Keeping this as an effect cleanup preserves rapid clicks inside one stable
  // board while disarming on route departure, account/org changes, date,
  // sport, market-kind, or YES/NO context changes.
  useEffect(() => {
    if (!oneClickAccess) disarmOneClick();
    return () => disarmOneClick();
  }, [
    oneClickAccess,
    authUserId,
    authOrgId,
    selectedSport,
    selectedSportsKey,
    selectedMarketType,
    selectedDate,
    marketSide,
    disarmOneClick,
  ]);

  const handleTradeClick = useCallback(
    (
      gameKey: string,
      side: 'away' | 'home',
      venue: 'kalshi' | 'polymarket',
      opts?: {
        priceCents?: number;
        initialOrderSide?: 'yes' | 'no';
        postOnly?: boolean;
        interactionSequence?: number;
      },
    ) => {
      const game = games.find((g) => g.key === gameKey);
      if (!game) return;

      const initialOrderSide = opts?.initialOrderSide ?? marketSide;
      const book =
        side === 'away'
          ? (venue === 'kalshi' ? game.kalshi?.away : game.polymarket?.away)
          : (venue === 'kalshi' ? game.kalshi?.home : game.polymarket?.home);
      const prefillPrice =
        opts?.priceCents ?? bestPriceCentsForOrderSide(book, initialOrderSide);

      // Trade modal was removed 2026-05-22 (deleted 2026-07-10) — the
      // GameDetailPanel side panel is the single trade-ticket surface
      // now. setExpandedGameKey opens the panel; tradeIntent carries the
      // click's pre-fill data so the ticket lands ready-to-submit.
      //
      // Clearing spread/total state on a moneyline-pill click is
      // belt-and-suspenders: even though `expandedGame` already prefers
      // the moneyline game when expandedGameKey resolves, the panel's
      // betLineContext check is identity-based and any stale
      // spreadTotalGame ref hanging around made the totals panel
      // "stick" after the user moved to a moneyline bet. Wipe both so
      // the panel switches cleanly into moneyline mode.
      setSpreadTotalGame(null);
      setSpreadTotalBetCtx(null);
      setExpandedGameKey(gameKey);
      setTradeIntent({
        side,
        venue,
        prefillPrice,
        initialOrderSide,
        postOnly: opts?.postOnly ?? false,
        interactionSequence: opts?.interactionSequence,
      });
      // Pill click also flips the page-level YES/NO toggle to match.
      // Default is NO, so this only triggers when a trader clicks a
      // green YES pill — the table + panel then read consistently
      // from the toolbar's YES state instead of asking the user to
      // toggle separately.
      if (opts?.initialOrderSide && opts.initialOrderSide !== marketSide) {
        setMarketSide(opts.initialOrderSide);
      }
    },
    [games, marketSide],
  );

  /** The pill / ladder click path the tables call. Identical to
   *  `handleTradeClick` except that it first cancels a focus still waiting on
   *  its row — same reasoning as `handleToggleExpand`. Hydration deliberately
   *  calls the RAW `handleTradeClick` below, so this dismissal is reachable
   *  only from a genuine user click and can never cancel the open it is about
   *  to perform.
   *
   *  One-click interception (live-tennis V1b): while the toolbar control is
   *  ARMED, a genuine Kalshi price click submits immediately through
   *  oneClickStore (same audited placeOrder lane) instead of opening the
   *  ticket. Deliberately gated on `opts.priceCents` — side clicks with no
   *  price still open the panel — and on this USER handler only, so deep-link
   *  hydration (raw handleTradeClick) can never place an order. */
  const handleUserTradeClick = useCallback(
    (...args: Parameters<typeof handleTradeClick>) => {
      const [gameKey, side, venue, opts] = args;
      if (oneClickAccess && oneClickArmed && venue === 'kalshi' && opts?.priceCents != null) {
        const game = games.find((g) => g.key === gameKey);
        if (game) {
          // D-020: the shared moneyline adapter REVALIDATES freshness /
          // availability / maker-role against the current book at submit
          // time, so an armed click racing a stale flip or a book move gets
          // a truthful local rejection instead of a blind submit. It also
          // owner-gates the hotkey-readiness blur: only a consumed click on
          // the currently focused card releases the pill's DOM focus.
          const consumed = tryOneClickFromMoneylineCtx({
            scope: LT2_DASH_ML_CARD_SCOPE,
            ctx: {
              game,
              teamSide: side,
              venue,
              initialOrderSide: opts.initialOrderSide ?? marketSide,
              prefillPrice: opts.priceCents,
              // Ladder bid clicks arrive postOnly=true (join, can't cross);
              // ask clicks may cross. Forward the click site's derivation.
              postOnly: opts.postOnly ?? false,
            },
          });
          if (consumed) return;
        }
      }
      // Leaving the eligible hot-click path is itself a safety boundary. A
      // Polymarket click or a non-price row opens the normal ticket and leaves
      // the page disarmed when the trader returns to Kalshi.
      if (oneClickArmed) disarmOneClick();
      dismissFocusRef.current();
      handleTradeClick(gameKey, side, venue, opts?.priceCents != null
        ? { ...opts, interactionSequence: ++tradeInteractionSequenceRef.current }
        : opts);
    },
    [
      handleTradeClick,
      oneClickAccess,
      oneClickArmed,
      disarmOneClick,
      games,
      marketSide,
    ],
  );

  // ── Focused deep-link hydration (#248) ─────────────────────────────
  // Below handleTradeClick on purpose: the focused link opens the SAME panel
  // a pill click opens, through the same handler, with the same audited
  // downstream path. There is no second order-entry route.
  const openFocusedMatch = useCallback<UseFocusedDashboardHydrationInput['openMatch']>(
    (match, target) => {
      // No price is calculated here — the URL layer never carries one, and
      // handleTradeClick derives the current book price itself.
      handleTradeClick(match.gameKey, match.teamSide, match.venue, {
        initialOrderSide: target.side,
        postOnly: false,
      });
    },
    [handleTradeClick],
  );

  // Polymarket's native YES token for the exact ticker, resolved from the live
  // market frame — never from the URL (#92's fail-closed orientation: a token
  // in a shareable link is an identity we can't verify). Undefined for Kalshi
  // targets, and undefined until the frame lands, which the matcher treats as
  // "cannot prove" rather than "guess".
  const getMarket = useMarketStore((s) => s.getMarket);
  const focusPolymarketNativeYesTokenId = useMemo(() => {
    void version;
    if (parsedFocus.kind !== 'market' || parsedFocus.target.venue === 'kalshi') return undefined;
    return getMarket(parsedFocus.target.ticker)?.yesTokenId ?? undefined;
  }, [parsedFocus, getMarket, version]);

  // #539 — a focused link naming a spread or total is resolved by the
  // Spread/Total board (which already holds its rows memoized) rather than
  // here, and reports its verdict back through this state. `undefined` for a
  // moneyline target, which is the signal for the hook to match `games` itself.
  const [lineFocusResolution, setLineFocusResolution] =
    useState<ExternalFocusResolution>('searching');
  const focusIsLineMarket =
    parsedFocus.kind === 'market' &&
    focusedBoardFor(parsedFocus.target.ticker) === 'spread-total';
  /** The one target handed to the Spread/Total board — or null when there is
   *  nothing for it to resolve. Derived ONCE so every focus prop below is
   *  gated identically: an earlier version gated the ticker on `focusTakenOver`
   *  but not the event/venue/side, which was harmless only by accident (the
   *  board early-returns without a ticker) and is exactly the kind of
   *  inconsistency that later grows into a bug. */
  const lineFocusTarget =
    focusIsLineMarket && !focusTakenOver && parsedFocus.kind === 'market'
      ? parsedFocus.target
      : null;
  // A new focus URL restarts the search. Without this a second link would
  // inherit the previous link's `opened` and skip hydration entirely.
  const focusIdentity = parsedFocus.kind === 'none' ? '' : parsedFocus.identity;
  useEffect(() => {
    setLineFocusResolution('searching');
  }, [focusIdentity]);

  const focusHydration = useFocusedDashboardHydration({
    focus: focusTakenOver ? NO_FOCUS : parsedFocus,
    connected,
    games,
    polymarketNativeYesTokenId: focusPolymarketNativeYesTokenId,
    externalResolution: focusIsLineMarket ? lineFocusResolution : undefined,
    openMatch: openFocusedMatch,
  });
  const dismissFocus = focusHydration.dismiss;
  useEffect(() => {
    dismissFocusRef.current = dismissFocus;
  }, [dismissFocus]);

  const handleMultiOutcomeClick = useCallback(
    (
      event: MultiOutcomeEvent,
      outcome: SeriesOutcome,
      venue: 'kalshi' | 'polymarket',
      opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
    ) => {
      const initialOrderSide: MarketSide = opts?.initialOrderSide ?? marketSide;
      const consumed = tryOneClickFromMultiOutcomePrice({
        outcome,
        venue,
        side: initialOrderSide,
        priceCents: opts?.priceCents,
        postOnly: opts?.postOnly ?? false,
      });
      if (consumed) return;

      // Same safety boundary as the binary table: leaving an eligible armed
      // Kalshi price path for a row, Polymarket, or ordinary ticket action
      // disarms before continuing.
      if (oneClickArmed) disarmOneClick();

      const game = synthesizeFromMultiOutcome(event, outcome);
      // Row click (no pill opts) on the already-open outcome toggles it
      // closed — mirrors handleToggleExpand on the binary table.
      if (!opts && expandedGameKey === game.key) {
        handleCloseDetail();
        return;
      }
      const book = venue === 'kalshi' ? outcome.kalshi : outcome.polymarket;
      // Same side semantics as the binary table: a row expand inherits the
      // toolbar's current YES/NO; only an explicit Y/N pill (or ladder)
      // click carries its own side. (The old card-grid UI defaulted row
      // clicks to YES and flipped the toolbar — jarring once soccer rows
      // looked and behaved like every other game row.)
      const prefillPrice =
        opts?.priceCents ?? bestPriceCentsForOrderSide(book ?? undefined, initialOrderSide);
      setSpreadTotalGame(game);
      setSpreadTotalBetCtx(buildMultiOutcomeBetContext(event, outcome));
      setExpandedGameKey(game.key);
      setTradeIntent({
        side: 'away',
        venue,
        prefillPrice,
        initialOrderSide,
        postOnly: opts?.postOnly ?? false,
        gameKey: game.key,
        interactionSequence:
          opts?.priceCents != null ? ++tradeInteractionSequenceRef.current : undefined,
      });
      // Toolbar follows explicit pill clicks only — same rule as
      // handleTradeClick on the binary table.
      if (opts?.initialOrderSide && opts.initialOrderSide !== marketSide) {
        setMarketSide(opts.initialOrderSide);
      }
    },
    [marketSide, expandedGameKey, handleCloseDetail, oneClickArmed, disarmOneClick],
  );

  const expandedGame = useMemo(() => {
    if (expandedGameKey) {
      // expandedGameKey may be stale across market-type switches (e.g.
      // user clicked a moneyline row, then switched to spread/total
      // tab — the moneyline key won't appear in the new `games` list).
      // Don't return null in that case; fall through to spreadTotalGame
      // so the panel still opens for a fresh spread/total row click.
      const fromGames = games.find((g) => g.key === expandedGameKey);
      if (fromGames) return fromGames;
      // Multi-outcome (soccer) synth keys aren't in `games` — re-derive the
      // synth fresh from the live event each tick so the panel's books track
      // the feed (the panel renders straight off the game prop; the stored
      // spreadTotalGame below is a click-time snapshot). Falls through to the
      // snapshot when the event leaves the store (e.g. post-settle prune).
      const sep = expandedGameKey.indexOf('|multi|');
      if (sep >= 0) {
        const eventKey = expandedGameKey.slice(0, sep);
        const code = expandedGameKey.slice(sep + '|multi|'.length);
        const event = multiEvents.find((e) => e.key === eventKey);
        const outcome = event?.outcomes.find((o) => o.code === code);
        if (event && outcome) return synthesizeFromMultiOutcome(event, outcome);
      }
    }
    // SpreadsTotals synthesizes a GameRow for the active expanded line
    // and pushes it here via onActiveGameChange. Same detail panel
    // renders against either source.
    return spreadTotalGame;
  }, [expandedGameKey, games, spreadTotalGame, multiEvents]);

  const seriesTicker = useMemo(() => {
    if (selectedMarketType === 'moneyline' && selectedSports.size !== 1) {
      return '';
    }
    const map: Record<string, string> = {
      nba: 'KXNBAGAME',
      wnba: 'KXWNBAGAME',
      nhl: 'KXNHLGAME',
      cbb: 'KXNCAAMBGAME',
      cfb: 'KXNCAAFGAME',
      atp: 'KXATPMATCH / KXITFMATCH',
      wta: 'KXWTAMATCH / KXITFWMATCH',
      mlb: 'KXMLBGAME',
      ufc: 'KXUFCFIGHT',
      esports: 'KXCS2GAME / KXDOTA2GAME / KXVALORANTGAME / KXLOLGAME',
      boxing: 'KXBOXING',
      cricket: 'KXWT20MATCH',
      soccer: 'KXWCGAME / KXUECLGAME / KXINTLFRIENDLYGAME',
    };
    return map[selectedSport] ?? '';
  }, [selectedSport, selectedMarketType, selectedSports.size]);

  // ── Date navigation ─────────────────────────────────────────────────
  // The day prev/next buttons always force single-day mode; navigating a
  // date while filtered to "all" is meaningless (the filter ignores the
  // date entirely).
  const handleDatePrev = useCallback(() => {
    takeOverFocus();
    setSelectedDate((d) => shiftMdy(d, -1));
    setDateFilter('today');
  }, [takeOverFocus]);

  const handleDateNext = useCallback(() => {
    takeOverFocus();
    setSelectedDate((d) => shiftMdy(d, 1));
    setDateFilter('today');
  }, [takeOverFocus]);

  // The remaining toolbar controls that mean "I'm driving now" (#248). The
  // raw setters are no longer passed straight to DashboardToolbar: a focused
  // link must not re-assert its date or market type over a deliberate change.
  const handleDateChange = useCallback(
    (next: string) => {
      takeOverFocus();
      setSelectedDate(next);
    },
    [takeOverFocus],
  );

  const handleDateFilterChange = useCallback(
    (next: DateFilter) => {
      takeOverFocus();
      setDateFilter(next);
    },
    [takeOverFocus],
  );

  const handleMarketTypeChange = useCallback(
    (next: MarketType) => {
      takeOverFocus();
      setSelectedMarketType(next);
    },
    [takeOverFocus],
  );

  // "Today" jumps the calendar back to the actual current day AND
  // enforces single-day mode, regardless of where the user has
  // navigated.
  const handleTodayClick = useCallback(() => {
    takeOverFocus();
    setSelectedDate(todayMdy());
    setDateFilter('today');
  }, [takeOverFocus]);

  const handleSportToggle = useCallback(
    (sport: DashboardSport) => {
      takeOverFocus();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('sport', sport);
          return next;
        },
        { replace: true },
      );
      // A sport switch is a context switch: close the detail panel and
      // collapse the expanded row instead of letting click-time state
      // linger. Without this, a soccer panel survived onto the next sport
      // via the spreadTotalGame fallback (frozen data), and a binary row
      // silently re-expanded when you returned to its sport — and the
      // still-mounted GameDetailPanel paid per-tick renders + held its
      // orderbook subs right through the switch's snapshot burst.
      if (sport !== selectedDashboardSport || allSports) {
        handleCloseDetail();
      }
      // Multi-outcome sports (soccer) now have BOTH the 3-way moneyline and the
      // full-game spread/total surfaces, but no futures/series table — so only
      // yank the tab back to moneyline when leaving a 'series' selection that
      // would blank for soccer. Moneyline and spread-total both render, so they
      // carry over on switch, mirroring the binary sports.
      if (MULTI_OUTCOME_SPORTS.has(underlyingSport(sport)) && selectedMarketType === 'series') {
        setSelectedMarketType('moneyline');
      }
    },
    [setSearchParams, selectedDashboardSport, allSports, handleCloseDetail, selectedMarketType, takeOverFocus],
  );

  const handleAllSportsClick = useCallback(() => {
    takeOverFocus();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sport', 'all');
        return next;
      },
      { replace: true },
    );
    if (!allSports) handleCloseDetail();
  }, [setSearchParams, allSports, handleCloseDetail, takeOverFocus]);

  // ── Saved games section (#120) ─────────────────────────────────────
  // `games` already has the sport / date / status filters applied upstream
  // (getGames), so filtering it here is what makes the Saved section respect
  // the toolbar rather than pierce it: a saved NBA game doesn't appear while
  // you're on the MLB tab, it reappears when you switch back.
  //
  // Every prop except `games` / `titleLabel` is shared with the main table so
  // a saved row behaves identically to its copy below — same pills, same
  // expansion, same trade routing. Spread rather than duplicated so the two
  // tables cannot drift apart.
  const gameTableCommonProps = {
    marketType: selectedMarketType,
    marketSide,
    priceMode,
    expandedGameKey,
    expandedTeamSide: tradeIntent?.side ?? null,
    onToggleExpand: handleToggleExpand,
    onTradeClick: handleUserTradeClick,
    positionByTicker: userActivity.positionByTicker,
    // Stable human display lane. The badge beside the toolbar discloses its
    // vintage; trading, alerts, and automation remain on the strict map.
    ordersByTicker: humanOrdersByTicker,
    botPresenceByTicker: edgeBots.byTicker,
    positionGameKeys,
    // #192/#212 — `hiddenEmptySports` is deliberately NOT shared: the two
    // tables disagree about it (Saved is exempt), and a shared default plus a
    // per-site override would silently invert on a prop-order edit. Each
    // GameTable below passes it explicitly. The evidence props stay shared —
    // they're inputs to the predicate, identical for both tables.
    activityReady,
    activeKalshiTickers,
    activePolyMarketIds,
  };

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar row — DashboardToolbar grows to fill the table column;
          PositionsWidget is a fixed w-96 container occupying exactly the
          right column above the order panel (same width, same left
          divider, same right edge). */}
      <div className="border-border flex items-stretch border-b">
        <div className="min-w-0 flex-1 p-3">
          <DashboardToolbar
            selectedSport={selectedDashboardSport}
            selectedSports={selectedDashboardSports}
            activeSports={activeDashboardSports}
            selectedMarketType={selectedMarketType}
            selectedDate={selectedDate}
            isToday={dateFilter === 'today' && selectedDate === todayMdy()}
            dateFilter={dateFilter}
            statusFilter={statusFilter}
            marketSide={marketSide}
            priceMode={priceMode}
            showAllSportsChip={showAllSportsChip}
            eligibleEmptySports={eligibleEmptySports}
            hiddenEmptySports={hiddenEmptySports}
            onToggleHiddenEmptySport={toggleHiddenEmptySport}
            onToggleAllEmptySports={toggleAllEmptySports}
            cfbBoardSelected={cfbBoardSelected}
            cfbSavedOnlyEligible={cfbSavedOnlyEligible}
            cfbSavedOnly={cfbSavedOnly}
            onToggleCfbSavedOnly={toggleCfbSavedOnly}
            kalshiMarketsOnlyEligible={isMoneylineBoard}
            kalshiMarketsOnly={kalshiMarketsOnly}
            onToggleKalshiMarketsOnly={toggleKalshiMarketsOnly}
            onSportToggle={handleSportToggle}
            onAllSportsClick={handleAllSportsClick}
            onMarketTypeChange={handleMarketTypeChange}
            onDateChange={handleDateChange}
            onDatePrev={handleDatePrev}
            onDateNext={handleDateNext}
            onDateFilterChange={handleDateFilterChange}
            onTodayClick={handleTodayClick}
            onStatusFilterChange={setStatusFilter}
            onMarketSideChange={setMarketSide}
            onPriceModeChange={setPriceMode}
          />
          {humanOrdersByTicker.size > 0 && (
            <QueueVintageBadge status={userActivity.queueStatus} className="mt-2 inline-flex" />
          )}
        </div>
        {/* One-click arm control — its own bordered cell so the armed state
            reads as a page-level mode, not a filter chip. */}
        {oneClickAccess && (
          // min-w-0 (not shrink-0): the ack line inside is a bounded WRAPPING
          // region, and the cell must be allowed to shrink instead of
          // stretching the toolbar row when the ack text is long. The arm
          // button and size input keep their own shrink-0.
          <div className="border-border flex min-w-0 items-center gap-2 border-l px-3">
            <OneClickArmControl />
            {/* Global A/D target readout — where the hotkeys would land,
                from the same focus store the keypress fires through. */}
            <Lt2TargetReadout />
            {/* D-020 V2B: A/D hotkey listener — dark unless the trader has
                lt2Access AND flipped the hotkey setting on. */}
            {lt2Access && lt2HotkeysEnabled && <Lt2HotkeyListener />}
          </div>
        )}
        <div className="border-border w-96 shrink-0 border-l">
          <PositionsWidget
            expanded={showPositionSidebar}
            onToggle={() => setShowPositionSidebar((v) => !v)}
          />
        </div>
      </div>

      {/* Content row — game table + detail panel side-by-side, both below toolbar */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto p-4">
          {/* #248 — a focused link that resolved to anything other than ONE
              exact market says so here, beside the table, and stays said.
              Not a toast: a trader who followed a link needs to still be able
              to read why no ticket opened after looking away. */}
          {focusHydration.message && (
            <div
              role="status"
              className="border-amber-500/30 bg-amber-500/10 text-amber-200 light:text-amber-700 mb-3 rounded-md border px-3 py-2 text-xs"
            >
              {focusHydration.message}
            </div>
          )}
          {isMultiOutcomeSport ? (
            multiEvents.length > 0 ? (
              <>
                {usingMultiUpcomingFallback && (
                  <div className="border-blue-500/30 bg-blue-500/10 text-blue-200 light:text-blue-700 mb-3 rounded-md border px-3 py-2 text-xs">
                    No {sportFilterLabel} games on {selectedDate}.{' '}
                    {multiUpcomingFallbackDate
                      ? `Showing the next day with games — ${isoToMdy(multiUpcomingFallbackDate)}.`
                      : 'Showing the next day with games.'}
                  </div>
                )}
                <MultiOutcomeTable
                  events={multiEvents}
                  titleLabel={tableTitleLabel}
                  seriesTicker={seriesTicker}
                  marketSide={marketSide}
                  priceMode={priceMode}
                  expandedOutcomeKey={expandedGameKey}
                  onOutcomeClick={handleMultiOutcomeClick}
                  positionByTicker={userActivity.positionByTicker}
                  ordersByTicker={humanOrdersByTicker}
                />
              </>
            ) : (
              <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                {connected ? (
                  <span>No {sportFilterLabel} events</span>
                ) : (
                  <>
                    <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                    <span>Connecting to Hydra...</span>
                  </>
                )}
              </div>
            )
          ) : selectedMarketType === 'series' ? (
            seriesEvents.length > 0 ? (
              // One board per event — a tennis sport can hold several live
              // tournaments (Wimbledon final + US Open field) whose legs
              // must never co-mingle in a single click-to-trade list.
              <div className="space-y-4">
                {seriesEvents.map((event) => (
                  <SeriesTable key={event.key} event={event} />
                ))}
              </div>
            ) : (
              <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                {connected ? (
                  <span>No {selectedSport.toUpperCase()} series markets</span>
                ) : (
                  <>
                    <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                    <span>Connecting to Hydra...</span>
                  </>
                )}
              </div>
            )
          ) : selectedMarketType === 'spread-total' ? (
            <SpreadsTotals
              sport={selectedSport}
              // #653 — this board reads its own rows, so the CFB bucket has to
              // travel as a prop; the moneyline path filters upstream instead.
              cfbBucket={selectedCfbBucket}
              boardLabel={selectedCfbBucketLabel ? sportFilterLabel : undefined}
              statusFilter={statusFilter}
              dateFilter={dateFilter}
              selectedDateIso={selectedDateIso}
              marketSide={marketSide}
              priceMode={priceMode}
              onActiveGameChange={setSpreadTotalGame}
              onActiveBetLineContextChange={setSpreadTotalBetCtx}
              onTradeClick={(data) => {
                // D-020: armed + entitled Kalshi price clicks submit
                // immediately through the shared intercept (same audited
                // lane, submit-time guard) instead of seeding the ticket.
                // Hydration seeds carry no prefillPrice and can never fire.
                if (
                  tryOneClickFromTicketCtx({
                    ctx: data,
                    surface: 'dash-spread-total',
                    period: 'full',
                    priceMode,
                  })
                ) {
                  return;
                }
                // Leaving the hot path on a genuine priced click disarms —
                // the V1 safety boundary, applied only under lt2 (review
                // finding, 2026-08-18): with the flag off these clicks never
                // entered the hot path, and V1 left the page armed here.
                // Price-less seeds (hydration) leave the arm state alone.
                if (lt2Access && oneClickArmed && data.prefillPrice != null) disarmOneClick();
                // Side panel already opened via onActiveGameChange; this
                // hand-off carries the click's pre-fill (price + side)
                // into the ticket via tradeIntent. The synthesized
                // GameRow has identical away/home books so `teamSide`
                // collapses to 'away' here.
                setTradeIntent({
                  side: data.teamSide,
                  venue: data.venue,
                  prefillPrice: data.prefillPrice,
                  initialOrderSide: data.initialOrderSide,
                  postOnly: data.postOnly,
                  // Tag the prefill with the line it came from. Selecting a
                  // different event swaps the panel's game without a fresh
                  // click; the stale price is then ignored so the ticket
                  // seeds the new book's best bid. See TradeIntent.gameKey.
                  gameKey: data.game.key,
                });
                // Page-level YES/NO toggle follows the pill the trader
                // just clicked — green YES pill flips the toolbar to
                // YES so subsequent reads are consistent.
                if (data.initialOrderSide && data.initialOrderSide !== marketSide) {
                  setMarketSide(data.initialOrderSide);
                }
              }}
              closeSignal={stCloseSignal}
              // #539 — only a LINE target is handed down. A moneyline focus is
              // matched by the hydration hook against `games`; passing its
              // ticker here too would have both paths racing to open a panel.
              // `focusTakenOver` withholds it once the trader has moved the
              // board themselves, matching the moneyline path's behavior.
              focusTicker={lineFocusTarget?.ticker ?? null}
              focusEventIdentity={lineFocusTarget?.eventIdentity ?? null}
              focusVenue={lineFocusTarget?.venue ?? null}
              focusSide={lineFocusTarget?.side}
              onFocusResolution={setLineFocusResolution}
            />
          ) : (
            /* Saved section sits OUTSIDE the has-games branch on purpose: a
             * saved game whose board row is gone is exactly the one the user
             * needs to remove, and filtering to an empty sport/date must not
             * take the remove control away with it. */
            <>
              {/* Above the Saved section, not below it: the fallback swaps the
                  date for the WHOLE board, Saved table included, so a reader who
                  meets the Saved rows first would otherwise have no reason to
                  doubt they're looking at the date in the toolbar. */}
              {usingUpcomingFallback && (
                <div className="border-blue-500/30 bg-blue-500/10 text-blue-200 light:text-blue-700 mb-3 rounded-md border px-3 py-2 text-xs">
                  No {sportFilterLabel} games on {selectedDate}.{' '}
                  {upcomingFallbackDate
                    ? `Showing the next day with games — ${isoToMdy(upcomingFallbackDate)}.`
                    : 'Showing the next day with games.'}
                </div>
              )}
              {savedRows.length > 0 && (
                <div className="mb-3">
                  <GameTable
                    games={savedRows}
                    // In saved-only mode this table IS the board, so it says so
                    // and names the slice the way the main board would
                    // ("CFB · FBS · Saved (3)"). Outside that mode it stays the
                    // shortlist strip above a full board and keeps its old,
                    // unqualified title.
                    titleLabel={
                      savedOnlyActive
                        ? `${sportFilterLabel} · Saved (${savedRows.length})`
                        : `Saved (${savedRows.length})`
                    }
                    // Blank, not the board's ticker: the header renders it
                    // beside the title, and "Saved — KXMLBGAME" reads as a
                    // claim about the saved set that isn't true of it.
                    seriesTicker=""
                    {...gameTableCommonProps}
                    // #192/#212 — Saved is an explicit user intent, so the
                    // empty-row cleanup never reaches it: the saved matchup
                    // with no book is exactly the one the user needs to see
                    // (and to un-save). The title above counts savedRows
                    // BEFORE GameTable filters, so a filtering Saved table
                    // rendered "Saved (1)" over an empty body.
                    hiddenEmptySports={NO_HIDDEN_EMPTY_SPORTS}
                    // #509 — Saved opts out of the Kalshi filter for the same
                    // reason it opts out of empty-row suppression: a saved
                    // matchup the user deliberately bookmarked must not vanish
                    // because its Kalshi book went quiet. Passed explicitly
                    // rather than relying on the prop default so a future
                    // change to that default cannot silently filter Saved.
                    kalshiMarketsOnly={false}
                    // #654 — Saved opts out of the minimized lens for the third
                    // time on the same principle: a game you deliberately
                    // starred belongs in your shortlist even if you also
                    // minimized it off the full slate. Star and minimize are
                    // opposite verbs, and the shortlist is where star wins.
                    // Omitted, not passed — there is no lens here at all, so no
                    // minimize control appears on these rows either.
                    //
                    // #647 — in saved-only mode this table is the ONLY board, so
                    // the A/D hotkeys move here. The ambiguity that kept them on
                    // the main board (a game present in both tables
                    // double-registering one card id) cannot arise when only one
                    // table is rendered.
                    lt2MoneylineCardScope={
                      savedOnlyActive ? LT2_DASH_ML_CARD_SCOPE : undefined
                    }
                  />
                </div>
              )}
              {savedUnlisted.length > 0 && (
                <SavedUnlistedCard
                  entries={savedUnlisted}
                  selectedDateIso={selectedDateIso}
                  onRemove={(id) => removeSavedGame(id, authUserId)}
                />
              )}
              {savedRows.length === 0 && savedUnlisted.length === 0 && connected && (
                <div className="border-border/60 bg-card/40 text-muted-foreground mb-3 rounded-lg border px-4 py-2 text-xs">
                  {savedCount === 0
                    ? 'No saved games yet — click the bookmark beside a game’s date to keep it here.'
                    : savedCount === 1
                      ? 'Your saved game isn’t on this sport or date.'
                      : `None of your ${savedCount} saved games are on this sport or date.`}
                </div>
              )}
              {/* #654 — the saved-only explainer, in its OWN block rather than
                  inside the all-empty notice above. That notice is additionally
                  gated on `savedUnlisted.length === 0 && connected`, so a user
                  holding a hydra-pruned saved game (or briefly disconnected)
                  would get a hidden board with nothing saying why — the exact
                  unexplained empty state this sentence exists to prevent. It
                  keys only on "saved-only is on and this board has no saved
                  rows", which is the condition that actually needs explaining.
                  The sentence names the control that emptied the board and the
                  bucket it is stacked on ("CFB · FCS"), so composing the two
                  filters cannot produce a silent blank. The turn-off is a
                  button, not a pointer at the toolbar. */}
              {savedOnlyActive && savedRows.length === 0 && (
                <div className="border-border/60 bg-card/40 text-muted-foreground mb-3 rounded-lg border px-4 py-2 text-xs">
                  Saved only is on, so the rest of the {sportFilterLabel} board is hidden.{' '}
                  <button
                    type="button"
                    onClick={toggleCfbSavedOnly}
                    className="text-primary hover:text-primary/80 rounded font-semibold underline underline-offset-2 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
                  >
                    Show the full board
                  </button>
                </div>
              )}
              {/* #654 — saved-only hides the full board rather than filtering
                  it down to the saved rows. The Saved table above already IS
                  that list, and it is the better surface for it: it carries the
                  saved games hydra has pruned (SavedUnlistedCard), it refuses to
                  let a quiet book hide a game you starred, and its empty state
                  already distinguishes "nothing saved" from "nothing saved HERE".
                  Filtering the main board instead would reimplement all three
                  beside code that already does them, and render the same rows
                  twice while it did. */}
              {savedOnlyActive ? null : games.length > 0 || archivedLoading ? (
                <GameTable
                  games={games}
                  titleLabel={tableTitleLabel}
                  seriesTicker={seriesTicker}
                  {...gameTableCommonProps}
                  hiddenEmptySports={hiddenEmptySports}
                  kalshiMarketsOnly={kalshiMarketsOnlyActive}
                  minimizedLens={minimizedLens}
                  archivedLoading={archivedLoading}
                  // #647: A/D hotkey focus on the standard moneyline rows.
                  // Main board only — wiring Saved too would double-register
                  // a game that is on both (ambiguous focus refuses). The one
                  // exception is saved-only mode, where this table isn't
                  // rendered at all; see the Saved table above.
                  lt2MoneylineCardScope={LT2_DASH_ML_CARD_SCOPE}
                />
              ) : (
                <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
                  {connected ? (
                    <span>No {sportFilterLabel} games</span>
                  ) : (
                    <>
                      <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
                      <span>Connecting to Hydra...</span>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail panel — sibling of the table, both below the toolbar.
         *  Key off the active game so switching between moneyline and
         *  spread/total expansions remounts the panel cleanly. */}
        {expandedGame && (
          <ExpandedGamePanel
            game={expandedGame}
            onClose={handleCloseDetail}
            tradeIntent={tradeIntent}
            marketSide={marketSide}
            onTeamSideChange={(side) => {
              setTradeIntent((t) => (t ? { ...t, side } : null));
            }}
            betLineContext={
              // Identity check for spread/total (the synth is pushed and held
              // by reference); multi-outcome synths are re-derived fresh each
              // tick, so match on the key instead.
              spreadTotalGame === expandedGame || expandedGame.key.includes('|multi|')
                ? spreadTotalBetCtx
                : null
            }
          />
        )}
      </div>

      {/* The bottom bar (display toggles + Hydra feed inspection) was promoted
          to the global <StatusBar/> in AuthShell — content-width, on every
          screen, fused with the rate-limit/tier account cluster. */}

      {/* Position popup — fixed-position overlay, can coexist with the
          detail panel (used to fight it for the right rail). */}
      {showPositionSidebar && (
        <PositionSidebar
          onClose={() => setShowPositionSidebar(false)}
          onOpenDetail={
            onOpenPositionsPage
              ? () => {
                  setShowPositionSidebar(false);
                  onOpenPositionsPage();
                }
              : undefined
          }
        />
      )}

      {/* Trade modal removed 2026-05-22 (files deleted 2026-07-10). All
          trade entry now happens inside the GameDetailPanel side panel
          rendered above. */}
    </div>
  );
}

/** Thin wrapper around GameDetailPanel that pulls the Unabated
 *  consensus from the batched gameStates poll and threads it into the
 *  panel's `unabated` prop. Extracted so the `useGameStateFor` hook
 *  call has a stable mount lifetime — putting the hook directly in
 *  ValueDashboard would mean it runs on every render whether or not
 *  any panel is open. Same swap-by-team-code logic the GameDetail page
 *  uses: Unabated's away/home can disagree with the canonical alpha-
 *  sorted GameRow, so we align by team code before mapping.
 *  Exported: PositionTracker mounts the same panel as its right-rail
 *  buy window so both surfaces share one ticket + consensus wiring. */
export function ExpandedGamePanel({
  game,
  onClose,
  tradeIntent,
  marketSide,
  onTeamSideChange,
  betLineContext,
}: {
  game: GameRow;
  onClose: () => void;
  tradeIntent: TradeIntent | null;
  marketSide: MarketSide;
  onTeamSideChange: (side: TeamSide) => void;
  betLineContext: BetLineContext | null;
}) {
  // For spread/total synth keys (`<eventKey>|spread|<ticker>`) we want
  // the eventKey portion for the gameStates lookup, not the synth key
  // itself. Moneyline rows fall through unchanged. Shared with GameOpenOrders'
  // order matching so the synth→base boundary is defined in exactly one place.
  const lookupBase = baseGameKey(game.key);
  // Live (doubleheader-safe) key — see the consensus note below; the poll and
  // the WS consensus must agree on the v2 key or a doubleheader's two rows
  // share one score.
  const hydraKey = toHydraGameKeyLive(lookupBase);
  const gameState = useGameStateFor(hydraKey);
  // Prefer the live WS consensus (sub-second freshness via the `unabated`
  // channel, push on ≥1¢ shift). Fall back to the 5s gameStates poll
  // snapshot for cold-start and any game outside hydra's active consensus set.
  // Consensus is cached per the doubleheader-safe v2 key, which IS the row key,
  // so pass `lookupBase` (the row key minus any synth suffix) straight through —
  // a doubleheader's two games then read their own un-blended consensus.
  const consensusKey = lookupBase || null;
  const liveConsensus = useConsensusFor(consensusKey);
  const liveConsensusTrend = useConsensusTrendFor(consensusKey);
  // OP odds for the panel's "O" row — same key vocabulary as consensus,
  // separate store map. Gated on the user toggle so switching showOpOdds
  // off hides the row along with the dashboard column.
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const opSlice = useOpOddsFor(showOpOdds ? consensusKey : null);
  // The ONE fair-view builder (rules 4 + 6c) — no inline copy of the swap
  // rule, no local fair-value math. Null (neither consensus nor anchor
  // fair on the frame) hides the row.
  const opLastAnchor = useOpLastAnchorFairFor(showOpOdds ? consensusKey : null);
  const opOdds = opSlice ? buildOpFairView(opSlice, game.homeCode, opLastAnchor) : null;
  const opOddsTrend = useOpTrendFor(showOpOdds ? consensusKey : null);
  const unabated = (() => {
    // WS is now the sole source of consensus — hydra moved it off the REST
    // gameStates response as of a37c761. Snapshot's durable last-known cache
    // fills in stable pre-game markets that don't actively shift.
    const c = liveConsensus;
    if (!c) return null;
    // Same swap rule the GameDetail page applies — Unabated's awayTeam
    // can land in our homeCode slot after canonical-key alpha-sort.
    // swapVenueColumns propagates the same flip to K and P rows so all
    // three columns line up under the matching team-code headers.
    const swapped = c.awayTeam === game.homeCode;
    return {
      awayCents: swapped ? c.homeFairProbCents : c.awayFairProbCents,
      homeCents: swapped ? c.awayFairProbCents : c.homeFairProbCents,
      // The order-entry U row displays the tenth-accurate value when hydra
      // sends it (integer cents remain the fallback + the glow key).
      awayTenths: (swapped ? c.homeFairProbTenths : c.awayFairProbTenths) ?? null,
      homeTenths: (swapped ? c.awayFairProbTenths : c.homeFairProbTenths) ?? null,
      // Sub-cent trend + sharp anchor for the U row's glow treatments. The
      // trend keeps Unabated's own slots; the panel re-applies `swapped`.
      trend: liveConsensusTrend,
      swapped,
      sharpBookUsed: c.sharpBookUsed ?? null,
    };
  })();
  return (
    <GameDetailPanel
      key={game.key}
      game={game}
      onClose={onClose}
      tradeIntent={tradeIntent}
      marketSide={marketSide}
      onTeamSideChange={onTeamSideChange}
      betLineContext={betLineContext}
      unabated={unabated}
      opOdds={opOdds}
      opOddsTrend={opOddsTrend}
    />
  );
}

// Saved games that have no row on the board (#120). Rendered from persisted
// metadata ONLY — teams, date, sport. No price, no status, no book: a saved
// snapshot must never read as a live quote, so there is deliberately nothing
// here that could be mistaken for one. The bookmark is filled and removes the
// entry, which is what keeps an expired game removable.
function SavedUnlistedCard({
  entries,
  selectedDateIso,
  onRemove,
}: {
  entries: SavedGame[];
  selectedDateIso: string;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="border-border bg-card mb-3 rounded-lg border px-4 py-2">
      <div className="text-muted-foreground mb-1 text-[10px] font-semibold uppercase tracking-wider">
        Saved — not on this board
      </div>
      <ul>
        {entries.map((e) => (
          <li key={e.id} className="flex items-center gap-2 py-1 text-xs">
            <button
              type="button"
              onClick={() => onRemove(e.id)}
              aria-label={`Remove ${e.awayCode} @ ${e.homeCode} from saved games`}
              title="Remove from Saved games"
              className="text-foreground shrink-0 rounded p-0.5 transition-colors hover:text-red-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            >
              <Bookmark size={12} className="fill-current" />
            </button>
            <span className="text-muted-foreground w-20 shrink-0 font-mono">{e.date}</span>
            <span className="text-foreground">
              {e.awayCode} <span className="text-muted-foreground/40">@</span> {e.homeCode}
            </span>
            <span className="text-muted-foreground/60 uppercase">{e.sport}</span>
            {/* Two very different situations wear the same "no row on the
                board" symptom, and conflating them would misreport a healthy
                game as gone: a saved game for ANOTHER date is simply off-screen,
                whereas one whose own date is showing really has dropped out of
                both the live feed and the analytics window. */}
            <span className="text-muted-foreground/50 ml-auto italic">
              {e.date === selectedDateIso ? 'no longer listed' : 'not on this date'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Project an analytics archived game into the GameRow shape the table
// already consumes. Most fields are stub-empty — the renderer branches
// on `archived` and skips the live-row plumbing entirely. `key` is the
// canonical hydra gameKey (date|TEAM_A|TEAM_B), which is also what
// positionGameKeys is keyed on, so the archived row's position-highlight
// lookup works without any extra translation.
function archivedToGameRow(a: ArchivedGame): GameRow {
  return {
    key: a.gameKey,
    sport: a.sport as Sport,
    date: a.date,
    timePt: '',
    startTimeMs: null,
    gameState: 'final',
    finalWinnerSide: null,
    awayCode: a.teamA,
    homeCode: a.teamB,
    awayName: a.teamA,
    homeName: a.teamB,
    kalshi: null,
    polymarket: null,
    archived: {
      kalshiVolume: a.kalshiVolume,
      polyVolume: a.polyVolume,
    },
  };
}
